import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api-client.js';
import { Badge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { DataTable, type Column } from '../components/ui/data-table.js';
import { EmptyState } from '../components/ui/empty-state.js';
import { Input, Select } from '../components/ui/input.js';

type EventStatus = 'pending' | 'awaiting' | 'confirmed' | 'failed' | 'pending_approval';

interface EventReminder {
  channels: ('voice' | 'sms' | 'email')[];
  offset: string;
}

interface EventDeliveryStatus {
  voice?: string;
  sms?: string;
  email?: string;
}

interface Event {
  id: string;
  title: string;
  attendee_name?: string;
  attendee_phone: string;
  attendee_email?: string;
  start_at: string;
  end_at: string;
  reminders: EventReminder[];
  status: EventStatus;
  delivery_status: EventDeliveryStatus;
  parent_event_id?: string | null;
  created_at: string;
}

interface ListResponse {
  data: Event[];
  pagination: { next_cursor: string | null; has_more: boolean };
}

const STATUS_TONE: Record<EventStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  pending_approval: 'warning',
  pending: 'neutral',
  awaiting: 'warning',
  confirmed: 'success',
  failed: 'danger',
};

export function EventsPage(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | EventStatus>('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['events'],
    queryFn: () => api.get('/v1/events') as Promise<ListResponse>,
  });

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return data.data.filter((row) => {
      if (statusFilter && row.status !== statusFilter) return false;
      if (!s) return true;
      return [row.title, row.attendee_name, row.attendee_phone, row.attendee_email]
        .some((v) => v && v.toLowerCase().includes(s));
    });
  }, [data, search, statusFilter]);

  const hasFilter = !!search || !!statusFilter;

  const cancel = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/events/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/v1/admin/events/${id}/approve-booking`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
    },
  });

  const columns: Column<Event>[] = [
    {
      key: 'title',
      header: 'Title',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Link to={`/events/${row.id}`} className="font-medium hover:text-accent">
            {row.title}
          </Link>
          {row.parent_event_id && (
            <Badge tone="neutral">recurring</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'attendee',
      header: 'Attendee',
      render: (row) => (
        <div className="text-sm">
          {row.attendee_name && <div>{row.attendee_name}</div>}
          <code className="text-xs text-ink-muted">{row.attendee_phone}</code>
        </div>
      ),
    },
    {
      key: 'when',
      header: 'When',
      render: (row) => (
        <div className="text-sm">
          <div>{new Date(row.start_at).toLocaleDateString()}</div>
          <div className="text-xs text-ink-muted">
            {new Date(row.start_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      ),
      width: '120px',
    },
    {
      key: 'reminders',
      header: 'Reminders',
      render: (row) => {
        if (row.reminders.length === 0) return <span className="text-ink-muted">—</span>;
        const channels = new Set<string>();
        for (const r of row.reminders) for (const c of r.channels) channels.add(c);
        return (
          <div className="flex gap-1">
            {[...channels].map((c) => (
              <Badge key={c} tone="neutral">{c}</Badge>
            ))}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <Badge tone={STATUS_TONE[row.status]}>{row.status}</Badge>,
      width: '110px',
    },
    {
      key: 'actions',
      header: '',
      width: '150px',
      className: 'text-right',
      render: (row) => (
        <div className="flex justify-end gap-1">
          {row.status === 'pending_approval' && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => approve.mutate(row.id)}
              disabled={approve.isPending}
            >
              Approve
            </Button>
          )}
          <Link to={`/events/${row.id}/edit`}>
            <Button variant="ghost" size="sm">Edit</Button>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (window.confirm(`Cancel event "${row.title}"?`)) cancel.mutate(row.id);
            }}
            disabled={cancel.isPending}
          >
            Cancel
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Events</h1>
          <p className="text-sm text-ink-muted">
            Scheduled appointments with reminders. Recurring events expand into independent
            child occurrences at create time.
          </p>
        </div>
        <Link to="/events/new">
          <Button>New event</Button>
        </Link>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>All events</CardTitle>
          {data && (
            <span className="text-xs text-ink-muted">
              {hasFilter ? `${filtered.length} of ${data.data.length}` : data.data.length}
              {data.pagination.has_more && !hasFilter ? '+' : ''}
            </span>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {data && (
            <div className="flex gap-2 px-4 py-3 border-b border-border">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search title, attendee name, phone, email…"
                className="flex-1 min-w-0"
              />
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as '' | EventStatus)}
                className="w-44"
              >
                <option value="">All statuses</option>
                <option value="pending_approval">Pending approval</option>
                <option value="pending">Pending</option>
                <option value="awaiting">Awaiting</option>
                <option value="confirmed">Confirmed</option>
                <option value="failed">Failed</option>
              </Select>
              {hasFilter && (
                <button
                  type="button"
                  onClick={() => { setSearch(''); setStatusFilter(''); }}
                  className="text-sm text-ink-muted hover:text-ink px-2"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          {isLoading && <p className="p-4 text-sm text-ink-muted">Loading…</p>}
          {error && <ErrorBanner error={error} />}
          {data && (
            <DataTable
              data={filtered}
              columns={columns}
              rowKey={(r) => r.id}
              emptyState={
                hasFilter ? (
                  <EmptyState
                    title="No matches"
                    description="No events match the current filters. Try a different term or clear them."
                  />
                ) : (
                  <EmptyState
                    title="No events yet"
                    description="Create one with reminders to have the connector dispatch a call / SMS / email at the offset time."
                  />
                )
              }
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="m-4 rounded border border-danger bg-surface p-3 text-sm">
      <p className="font-medium text-danger">Failed to load</p>
      <p className="mt-1 text-ink-muted">
        {status ? `HTTP ${status}` : 'Network error'} — {(error as Error).message}
      </p>
    </div>
  );
}
